import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Download, FileText, AlertTriangle } from 'lucide-react';

/**
 * Reusable attachment preview modal for PDFs and images.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - title: string (display title in header)
 * - downloadName: string (suggested filename)
 * - fetchUrl: full URL to fetch the file (used for both preview blob and download)
 * - headers: object — auth headers for fetch
 * - mime?: optional pre-known MIME ("application/pdf", "image/...")
 */
const AttachmentPreviewModal = ({
  open,
  onClose,
  title = 'Attachment',
  downloadName = 'attachment',
  fetchUrl,
  headers = {},
  mime,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [blobUrl, setBlobUrl] = useState('');
  const [resolvedMime, setResolvedMime] = useState(mime || '');

  useEffect(() => {
    let revokedUrl = '';
    let cancelled = false;
    const controller = new AbortController();
    // Hard 25s timeout safety net — if anything hangs we still surface a useful error
    const timeoutId = setTimeout(() => {
      try { controller.abort(); } catch {}
    }, 25000);

    const load = async () => {
      if (!open || !fetchUrl) return;
      setLoading(true);
      setError('');
      setBlobUrl('');
      try {
        const fetchHeaders = { ...headers };
        delete fetchHeaders['Content-Type'];
        const res = await fetch(fetchUrl, {
          headers: fetchHeaders,
          signal: controller.signal,
        });
        if (!res.ok) {
          setError(`Failed to load attachment (status ${res.status})`);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        const detectedMime = blob.type || mime || '';
        // Reconstruct blob with explicit PDF mime so browsers reliably render it inline
        const looksLikePdf =
          detectedMime.includes('pdf') ||
          (downloadName || '').toLowerCase().endsWith('.pdf');
        const finalBlob = looksLikePdf
          ? new Blob([blob], { type: 'application/pdf' })
          : blob;
        const url = URL.createObjectURL(finalBlob);
        revokedUrl = url;
        setBlobUrl(url);
        setResolvedMime(looksLikePdf ? 'application/pdf' : detectedMime);
      } catch (e) {
        if (cancelled) return;
        if (e.name === 'AbortError') {
          setError('Preview timed out. Please try downloading the file instead.');
        } else {
          setError('Network error loading attachment');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      try { controller.abort(); } catch {}
      // Defer revoke so the iframe/object has time to render the blob URL
      if (revokedUrl) setTimeout(() => URL.revokeObjectURL(revokedUrl), 1000);
    };
  }, [open, fetchUrl]);

  const triggerDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const isPdf = resolvedMime?.includes('pdf') || downloadName?.toLowerCase().endsWith('.pdf');
  const isImage = resolvedMime?.startsWith('image/');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-5xl w-[95vw] h-[88vh] p-0 flex flex-col" data-testid="attachment-preview-modal">
        <DialogHeader className="px-6 py-3 pr-12 sm:pr-14 border-b flex flex-row items-center justify-between gap-3">
          <DialogTitle className="flex items-center gap-2 truncate min-w-0 flex-1">
            <FileText className="h-5 w-5 text-blue-600 shrink-0" />
            <span className="truncate" data-testid="preview-title">{title}</span>
          </DialogTitle>
          <Button
            size="sm"
            onClick={triggerDownload}
            disabled={!blobUrl || loading}
            data-testid="preview-download-btn"
            className="gap-2 shrink-0"
          >
            <Download className="h-4 w-4" /> Download
          </Button>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-muted/40 overflow-hidden">
          {loading && (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading preview...
            </div>
          )}
          {!loading && error && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-3">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
              <p className="text-sm text-muted-foreground" data-testid="preview-error">{error}</p>
              {blobUrl && (
                <Button size="sm" variant="outline" onClick={triggerDownload} className="gap-2">
                  <Download className="h-4 w-4" /> Download instead
                </Button>
              )}
            </div>
          )}
          {!loading && !error && blobUrl && isPdf && (
            <object
              data={`${blobUrl}#toolbar=1&navpanes=0&view=FitH`}
              type="application/pdf"
              className="w-full h-full"
              data-testid="preview-pdf-iframe"
            >
              {/* Fallback for browsers that won't render <object> inline */}
              <iframe
                src={blobUrl}
                title={title}
                className="w-full h-full border-0"
              />
              <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-3">
                <FileText className="h-10 w-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Your browser can't display this PDF inline. Use the buttons below.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => window.open(blobUrl, '_blank')} className="gap-2">
                    Open in new tab
                  </Button>
                  <Button size="sm" onClick={triggerDownload} className="gap-2">
                    <Download className="h-4 w-4" /> Download
                  </Button>
                </div>
              </div>
            </object>
          )}
          {!loading && !error && blobUrl && isImage && (
            <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
              <img
                src={blobUrl}
                alt={title}
                className="max-h-full max-w-full object-contain"
                data-testid="preview-image"
              />
            </div>
          )}
          {!loading && !error && blobUrl && !isPdf && !isImage && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-3">
              <FileText className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Preview not available for this file type. Please download to view.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentPreviewModal;
